// The shared lib is compiled from source by consumers whose tsconfig has no DOM
// lib (libs/api): pull the DOM types in here so the browser branch typechecks.
/// <reference lib="dom" />
import {
  PDF_TEMPLATE_IMAGE_JPEG_QUALITY,
  PDF_TEMPLATE_IMAGE_MAX_SIDE,
} from "./pdf-generation.constants";

const IMG_TAG_REGEX = /<img\b[^>]*>/gi;

const SRC_ATTRIBUTE_REGEX = /\bsrc=(["'])(.*?)\1/i;

export type ImageShrinker = (src: string) => Promise<string>;

export const fitWithinMaxSide = (
  width: number,
  height: number,
  maxSide: number
) => {
  const ratio = Math.min(1, maxSide / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

// jsPDF's html() embeds <img> bytes as-is, so this helper must only ever run
// in a browser: the shared lib is also bundled in the backend, where the
// template is never produced.
const isBrowserEnvironment = () =>
  typeof document !== "undefined" && typeof Image !== "undefined";

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Image could not be loaded: ${src}`));
    image.src = src;
  });

const hasTransparentPixels = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number
) => {
  const { data } = context.getImageData(0, 0, width, height);

  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 255) return true;
  }

  return false;
};

/**
 * Downscales an image to at most PDF_TEMPLATE_IMAGE_MAX_SIDE on its longest
 * side and re-encodes it as JPEG (PNG when it has transparent pixels, so a
 * logo keeps its background). Outside a browser the source is returned as is.
 */
export const shrinkImageForPdfTemplate: ImageShrinker = async (src) => {
  if (!isBrowserEnvironment()) return src;
  const image = await loadImage(src);

  const { width, height } = fitWithinMaxSide(
    image.naturalWidth,
    image.naturalHeight,
    PDF_TEMPLATE_IMAGE_MAX_SIDE
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  if (!context) return src;
  context.drawImage(image, 0, 0, width, height);

  if (hasTransparentPixels(context, width, height)) {
    return canvas.toDataURL("image/png");
  }

  return canvas.toDataURL("image/jpeg", PDF_TEMPLATE_IMAGE_JPEG_QUALITY);
};

export type InlinedSection = {
  html: string | null;
  removedSources: string[];
};

const shrinkWithFirstSuccessfulShrinker = async (
  source: string,
  shrinkers: ImageShrinker[]
) => {
  for (const shrink of shrinkers) {
    try {
      return await shrink(source);
    } catch (error) {
      console.warn("Custom section image could not be reduced", source, error);
    }
  }

  return null;
};

/**
 * Replaces the src of every <img> of a custom section with the data URL of
 * the first shrinker that reduces it. Other attributes are kept.
 */
export const inlineSectionImages = async (
  html: string | null,
  shrinkers: ImageShrinker[] = [shrinkImageForPdfTemplate]
): Promise<InlinedSection> => {
  if (!html) return { html, removedSources: [] };
  const tags = html.match(IMG_TAG_REGEX) ?? [];

  const sources = [
    ...new Set(
      tags
        .map((tag) => SRC_ATTRIBUTE_REGEX.exec(tag)?.[2])
        .filter((source): source is string => !!source)
    ),
  ];

  if (sources.length === 0) return { html, removedSources: [] };

  const replacements = new Map<string, string | null>();
  await Promise.all(
    sources.map(async (source) => {
      replacements.set(
        source,
        await shrinkWithFirstSuccessfulShrinker(source, shrinkers)
      );
    })
  );

  return {
    html: html.replace(IMG_TAG_REGEX, (tag) => {
      const source = SRC_ATTRIBUTE_REGEX.exec(tag)?.[2];

      if (!source) return tag;

      const dataUrl = replacements.get(source);

      if (!dataUrl) return "";

      return tag.replace(
        SRC_ATTRIBUTE_REGEX,
        (_, quote: string) => `src=${quote}${dataUrl}${quote}`
      );
    }),
    removedSources: sources.filter((source) => !replacements.get(source)),
  };
};
